"use client";

import { Loader2 } from "lucide-react";
import {
  useConnectionFreshnessStore,
  type ConnectionFreshness,
} from "@/vybe-trader-client";

/**
 * Connection freshness banner (sleep/wake freshness gate, proposal 23).
 *
 * Sits above the watchlist and reflects the single freshness signal on the
 * status-ok / status-warn / status-err palette — no new colours. Hidden while
 * the feed is healthy ("live") so it only surfaces on a suspect wake and the
 * ensuing recycle → resync.
 */

const COPY: Record<
  Exclude<ConnectionFreshness, "live">,
  { label: string; detail: string }
> = {
  stale: {
    label: "STALE",
    detail: "feed frozen — socket suspect after resume",
  },
  resyncing: {
    label: "RESYNCING",
    detail: "recycled socket — re-subscribe + snapshot backfill",
  },
};

export function ConnectionFreshnessBanner({
  isDesktop = false,
}: {
  isDesktop?: boolean;
}) {
  const freshness = useConnectionFreshnessStore((s) => s.freshness);

  // Healthy feed → no banner (matches the mock, which only shows it on wake).
  if (freshness === "live") return null;

  const isStale = freshness === "stale";
  const { label, detail } = COPY[freshness];

  // status-err for stale (frozen), status-warn for resyncing (recovery in flight).
  const tone = isStale
    ? "border-red-500/40 bg-red-500/10 text-red-500"
    : "border-yellow-500/40 bg-yellow-500/10 text-yellow-500";

  return (
    <div
      role="status"
      className={`flex shrink-0 items-center gap-2 border-b border-border font-mono ${
        isDesktop ? "px-2 py-1 text-[11px]" : "px-3 py-1.5 text-xs"
      }`}
    >
      <div
        className={`flex w-full items-center gap-2 rounded-md border px-2 py-1 ${tone}`}
      >
        {isStale ? (
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-500"
          />
        ) : (
          <Loader2 aria-hidden className="h-3 w-3 shrink-0 animate-spin" />
        )}
        <span className="font-semibold tracking-wide">{label}</span>
        <span className="truncate text-muted-foreground">{detail}</span>
      </div>
    </div>
  );
}
